import { DeleteOutlined, EyeOutlined, FilePdfOutlined } from '@ant-design/icons';
import { Modal } from '@components/common/Modal/Modal';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { ITableActionProps, PagingType, UTable } from '@components/common/UTable/UTable';
import { getLevelDepth } from '@components/HorizontalMenu/types';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router';

import { useGetOrganizationsShortQuery } from '@/api/organizations/api';
import {
  useCreateAutopsyActMutation,
  useDeleteAutopsyActMutation,
  useGetAutopsyActsQuery,
} from '@/api/pathology/api';
import { AutopsyActBrief } from '@/api/pathology/types';
import { ACTS, PATHOLOGY } from '@/constants/routes';
import { addSchema } from '@/pages/Pathology/components/acts/add.config';
import { columns } from '@/pages/Pathology/components/acts/columns.config';
import { filterSchema } from '@/pages/Pathology/components/acts/filters.config';
import {
  AudioSourceValues,
  PROCESSING_POLLING_INTERVAL,
  resolveActsOrganizationId,
  toAudioAttachment,
} from '@/pages/Pathology/helpers';
import { useAutopsyActPrint } from '@/pages/Pathology/hooks/useAutopsyActPrint';
import { Sort, UUID } from '@/shared/api/types';
import { OrganizationLevelType } from '@/shared/organizations/enums';
import {
  AutopsyActStatus,
  AutopsySource,
  isProcessingInProgress,
} from '@/shared/pathology/enums';
import { pathology } from '@/shared/security/permissions/pathology';
import { withSecurity } from '@/shared/security/withSecurity';
import { AppDispatch, RootState } from '@/store';
import { setPathologyActsFilters } from '@/store/filtersSlice';
import { setPathologyActsSort } from '@/store/sortSlice';
import DateMapper, { DateTimeFormat } from '@/utils/dateUtils';
import { mapTableIndexWithPage } from '@/utils/utils';

const defaultSort: Sort[] = [{ key: 'performedAt', order: 'desc' }];

type CreateFormValues = AudioSourceValues & {
  individualId: UUID;
  performedAt: string;
  notes?: string;
  transcript?: string;
};

const isFilled = (value: unknown) => !_.isNil(value) && value !== '';

export const AutopsyActs = withSecurity(({ auth }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();

  const queryOrganizationId = searchParams.get('organizationId');
  const organizationId = resolveActsOrganizationId(auth, queryOrganizationId);
  // Выше уровня хозяйства хозяйство выбирается в меню или фильтре
  const isAboveOrganization =
    getLevelDepth(auth.level) < getLevelDepth(OrganizationLevelType.ORGANIZATION);

  const filters: Record<string, any> = useSelector(
    (state: RootState) => state.filtersSlice.pathologyActs,
  );
  const setFilters = (value: Record<string, any>) =>
    dispatch(setPathologyActsFilters(value));
  const sort: Sort[] | undefined = useSelector(
    (state: RootState) => state.sorterSlice.pathologyActs,
  );
  const setSort = (value?: Sort[]) => dispatch(setPathologyActsSort(value));

  const [paging, setPaging] = useState<PagingType>({ page: 0, size: 10 });
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [pollingInterval, setPollingInterval] = useState<number>(0);

  const { data: organizations } = useGetOrganizationsShortQuery(
    { level: OrganizationLevelType.ORGANIZATION },
    { skip: !isAboveOrganization },
  );
  const {
    data: acts,
    currentData,
    isFetching,
  } = useGetAutopsyActsQuery(
    {
      filters: { ...filters, organizationId },
      paging,
      sort,
    },
    { pollingInterval, skipPollingIfUnfocused: true },
  );
  const [createAct] = useCreateAutopsyActMutation();
  const [deleteAct] = useDeleteAutopsyActMutation();
  const { print, printingId } = useAutopsyActPrint();

  // Пока хотя бы одна запись на странице обрабатывается, список обновляется сам
  useEffect(() => {
    const hasProcessing = !!acts?.content?.some((act) =>
      isProcessingInProgress(act.processingStage),
    );
    setPollingInterval(hasProcessing ? PROCESSING_POLLING_INTERVAL : 0);
  }, [acts]);

  const organizationName = useMemo(() => {
    if (!queryOrganizationId) return null;
    const organization = organizations?.find((item) => item.id === queryOrganizationId);
    return organization?.shortName || organization?.name || searchParams.get('orgName');
  }, [organizations, queryOrganizationId, searchParams]);

  const setOrganizationParam = useCallback(
    (value?: UUID) => {
      const params = new URLSearchParams(searchParams);
      params.delete('orgName');
      if (value) {
        params.set('organizationId', value);
      } else {
        params.delete('organizationId');
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleFilter = ({ organizationId: selectedOrganizationId, ...rest }: any) => {
    setFilters(_.pickBy(rest, isFilled));
    if (isAboveOrganization) setOrganizationParam(selectedOrganizationId);
  };

  const handleResetFilter = () => {
    setFilters({});
    if (isAboveOrganization) setOrganizationParam(undefined);
  };

  const handleCreate = async ({
    individualId,
    performedAt,
    notes,
    transcript,
    ...sourceValues
  }: CreateFormValues) => {
    try {
      const created = await createAct({
        data: {
          individualId,
          performedAt,
          notes: notes?.trim() || undefined,
          transcript:
            sourceValues.source === AutopsySource.TEXT ? transcript?.trim() : undefined,
        },
        audio: toAudioAttachment(sourceValues),
      }).unwrap();

      setIsAddModalOpen(false);
      navigate(PATHOLOGY + ACTS + `/${created.id}`);
    } catch {
      // ошибка уже показана в baseQueryWithReAuth
    }
  };

  const actions = (record: AutopsyActBrief): ITableActionProps[] => [
    {
      icon: <EyeOutlined />,
      tooltip: 'Открыть',
      action: (act: AutopsyActBrief) => navigate(PATHOLOGY + ACTS + `/${act.id}`),
      security: { permissions: pathology.act.view },
    },
    {
      icon: <FilePdfOutlined />,
      tooltip: 'Печать PDF',
      action: (act: AutopsyActBrief) => print(act),
      disabled: printingId === record.id,
      security: { permissions: pathology.act.print },
    },
    ...(record.status === AutopsyActStatus.DRAFT
      ? [
          {
            icon: <DeleteOutlined />,
            action: (act: AutopsyActBrief) => deleteAct({ id: act.id }),
            popconfirm: {
              title: 'Удалить черновик акта?',
              okText: 'Да',
              cancelText: 'Нет',
            },
            security: { permissions: pathology.act.edit },
          },
        ]
      : []),
  ];

  return (
    <>
      <PageTitle title={'Акты вскрытия'} />
      <UTable
        size={'small'}
        data={mapTableIndexWithPage(acts?.content, paging)}
        columns={columns}
        currentSort={sort}
        defaultSort={defaultSort}
        onChange={setSort}
        // при фоновом опросе таблица не мигает индикатором загрузки
        loading={isFetching && !currentData}
        paging={paging}
        total={acts?.totalElements}
        onPagingChange={setPaging}
        buttons={[
          {
            addBtn: true,
            text: 'Добавить акт',
            action: () => setIsAddModalOpen(true),
            security: { permissions: pathology.act.create },
          },
        ]}
        filter={{
          // хозяйство хранится в адресе (?organizationId=), остальные фильтры — в store
          filters: queryOrganizationId
            ? { ...filters, organizationId: queryOrganizationId }
            : filters,
          schema: filterSchema(organizations, isAboveOrganization),
          onFilter: handleFilter,
          onReset: handleResetFilter,
          underTitle: organizationName ? `Хозяйство: ${organizationName}` : null,
        }}
        actions={actions}
      />

      <Modal
        open={isAddModalOpen}
        mode={'form'}
        formKey={'addAutopsyAct'}
        title={'Добавить акт вскрытия'}
        data={{
          performedAt: DateMapper().format(DateTimeFormat.LOCAL_DATE),
          source: AutopsySource.UPLOAD,
        }}
        schema={addSchema(organizationId)}
        onSubmit={handleCreate}
        onCancel={() => setIsAddModalOpen(false)}
        width={'520px'}
        layout={{
          formLayout: 'vertical',
          itemLayout: 'vertical',
        }}
        buttons={{ submit: { text: 'Создать' } }}
        destroyOnHidden={true}
      />
    </>
  );
});
